import { Box, Button, Stack, Typography } from "@mui/material";
import { useLayoutEffect, useMemo, useRef, useState } from "react";
type KeyValueCollapsedPanelProps = {
  data: Record<string, unknown>;
};

function humanizeSnakeCase(input: string) {
  return input.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

function isHtmlString(value: unknown) {
  return typeof value === "string" && /<\/?[a-z][\s\S]*>/i.test(value);
}

function formatPrimitive(value: unknown) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return value.toLocaleString();
  if (typeof value === "string") return value.trim() === "" ? "—" : value;
  return String(value);
}

function ValueRenderer({ value }: { value: unknown }) {
  if (Array.isArray(value)) {
    if (value.length === 0) return <Typography variant="body2">—</Typography>;
    return (
      <Typography
        variant="body2"
        component="pre"
        sx={{ m: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
      >
        {JSON.stringify(value, null, 2)}
      </Typography>
    );
  }

  if (isHtmlString(value)) {
    return (
      <Typography
        variant="body2"
        sx={{
          "& p": { margin: 0, marginBottom: 0.75 },
          "& p:last-of-type": { marginBottom: 0 },
        }}
        dangerouslySetInnerHTML={{ __html: String(value) }}
      />
    );
  }

  return <Typography variant="body2">{formatPrimitive(value)}</Typography>;
}

export function FareharborItemDescription({
  data,
}: KeyValueCollapsedPanelProps) {
  const collapsedHeight = 300;
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const entries = useMemo(() => {
    return Object.entries(data ?? {}).filter(([_, value]) => {
      if (value === null || value === undefined) return false;
      if (typeof value === "string" && value.trim() === "") return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return true;
    });
  }, [data]);

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const check = () => {
      // measure full content height (independent of our maxHeight clamp)
      setIsOverflowing(el.scrollHeight > collapsedHeight + 1);
    };

    check();

    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsedHeight, entries]);

  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
        Description
      </Typography>

      <Box
        sx={{
          position: "relative",
          maxHeight: expanded ? "none" : `${collapsedHeight}px`,
          overflow: expanded ? "visible" : "hidden",
          transition: "max-height 180ms ease",
          borderRadius: 1.5,
        }}
      >
        <Stack ref={contentRef} spacing={1.5}>
          {entries.map(([rawKey, value]) => (
            <Box key={rawKey}>
              <Typography
                variant="body1"
                fontWeight={700}
                sx={{ fontSize: 14 }}
              >
                {humanizeSnakeCase(rawKey)}
              </Typography>

              <Box sx={{ mt: 0.25 }}>
                <ValueRenderer value={value} />
              </Box>
            </Box>
          ))}
        </Stack>

        {/* fade overlay when collapsed */}
        {!expanded && isOverflowing ? (
          <Box
            sx={{
              pointerEvents: "none",
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 88,
              background:
                "linear-gradient(to bottom, rgba(255,255,255,0), rgba(255,255,255,1))",
            }}
          />
        ) : null}
      </Box>

      {isOverflowing ? (
        <Box sx={{ mt: 1 }}>
          <Button size="small" onClick={() => setExpanded((v) => !v)}>
            {expanded ? "See less" : "See more"}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
